import product from 'immer'
import { createSelector, createSlice } from '@reduxjs/toolkit'

const initialState = {
  currentModal: null, // null | 'PAYMENT' | 'CONFIRMATION' | 'CANCEL'
  paymentMethod: 'CARD',
  paymentStatus: 'DEFAULT', // 'DEFAULT' | 'PROCESSING' | 'SUCCESS' | 'ERROR'
  paymentError: null,
  cart: [],
  lastScannedProduct: null,
  loyaltyCard: null, // loyalty card number or null
}

export const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    addToCart: (state, { payload }) => {
      const item = state.cart.find(o => o.id === payload.id)
      return item
        ? {
            ...state,
            lastScannedProduct: payload,
            cart: product(state.cart, draftState => {
              draftState.find(o => o.id === item.id).qty++
            })
          }
        : {
            ...state,
            lastScannedProduct: payload,
            cart: [...state.cart, { ...payload, qty: 1 }]
          }
    },
    removeFromCart: (state, { payload }) => {
      const itemToRemove = state.cart.find(o => o.id.toString() === payload.id.toString())
      return itemToRemove && itemToRemove.qty > 1 && !payload.all
        ? {
            ...state,
            cart: product(state.cart, draftState => {
              draftState.find(o => o.id === itemToRemove.id).qty--
            })
          }
        : {
            ...state,
            cart: state.cart.filter(o => o.id.toString() !== payload.id.toString())
          }
    },
    setCurrentModal: (state, { payload }) => {
      return { ...state, currentModal: payload }
    },
    setPaymentMethod: (state, { payload }) => {
      return { ...state, paymentMethod: payload }
    },
    setPaymentStatus: (state, { payload }) => {
      return { ...state, paymentStatus: payload }
    },
    setPaymentError: (state, { payload }) => {
      return { ...state, paymentError: payload }
    },
    setLastScannedProduct: (state, { payload }) => {
      return { ...state, lastScannedProduct: payload }
    },
    setLoyaltyCard: (state, { payload }) => {
      return { ...state, loyaltyCard: payload }
    },
    // Swap cart items for the same products from another catalogue (e.g. GBP -> PLN brand)
    switchCatalog: (state, { payload: products }) => {
      const byId = id => products.find(p => p.id === id)
      return {
        ...state,
        cart: state.cart
          .map(item => byId(item.id) && { ...byId(item.id), qty: item.qty })
          .filter(Boolean),
        lastScannedProduct: state.lastScannedProduct && (byId(state.lastScannedProduct.id) || null)
      }
    },
    reset: () => {
      return { ...initialState }
    }
  }
})

const cartItems = state => state.checkout.cart

export const {
  addToCart,
  removeFromCart,
  setCurrentModal,
  setPaymentMethod,
  setPaymentStatus,
  setPaymentError,
  setLastScannedProduct,
  setLoyaltyCard,
  switchCatalog,
  reset
} = checkoutSlice.actions

export const cartTotalSelector = createSelector(cartItems, cart =>
  cart.length > 0 ? cart.map(o => o.qty * o.price).reduce((acc, current) => acc + current) : 0
)

export const cartItemCountSelector = createSelector(cartItems, cart =>
  cart.length > 0 ? cart.map(o => o.qty).reduce((acc, current) => acc + current) : 0
)

const loyaltyCardState = state => state.checkout.loyaltyCard

export const finalTotalSelector = createSelector(
  [cartTotalSelector, loyaltyCardState],
  (cartTotal, loyaltyCard) => {
    const discount = loyaltyCard ? cartTotal * 0.10 : 0
    return cartTotal - discount
  }
)

export default checkoutSlice.reducer
